
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { LegacyJob } from '../types';
import { uploadResume, submitApplication, UploadResumeResponse } from '@/api/application';
import PhoneInput from './PhoneInput';
import { getDefaultCountryCode, formatPhoneNumber } from '@/utils/phoneUtils';
import {
  trackApplicationStarted,
  trackApplicationStepCompleted,
  trackResumeUploaded,
  trackApplicationSubmitted,
  trackApplicationAbandoned,
  trackFormValidationError,
  identifyUser,
  setUserProperties,
} from '../src/lib/analytics';

// 使用 LegacyJob 类型用于向后兼容
type Job = LegacyJob;

interface ApplicationFormProps {
  job: Job;
  onBack: () => void;
  onSubmit: (result?: any) => void;
}

type FormStep = 1 | 2;

// 内部 Step 组件 - 移到组件外部防止重新创建
interface Step1Props {
  formData: { email: string; phone: string; name: string };
  countryCode: string;
  errors: Record<string, string>;
  onChange: (field: string, value: string) => void;
  onCountryCodeChange: (code: string) => void;
}

const Step1: React.FC<Step1Props> = React.memo(({ formData, countryCode, errors, onChange, onCountryCodeChange }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl sm:text-2xl font-black text-slate-900 mb-2">{t('applicationForm.step1Title')}</h3>
        <p className="text-slate-500">{t('applicationForm.step1Desc')}</p>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-bold text-slate-600 uppercase tracking-widest ml-1">{t('applicationForm.nameLabel')}</label>
        <input
          type="text"
          required
          value={formData.name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder={t('applicationForm.namePlaceholder')}
          className={`w-full bg-slate-50 border ${errors.name ? 'border-red-400' : 'border-slate-100'} rounded-xl px-5 py-3.5 text-slate-900 font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all`}
        />
        {errors.name && <p className="text-[11px] font-bold text-red-500 ml-1">{errors.name}</p>}
      </div>

      <div className="space-y-2">
        <label className="text-xs font-bold text-slate-600 uppercase tracking-widest ml-1">{t('applicationForm.emailLabel')}</label>
        <input
          type="email"
          required
          value={formData.email}
          onChange={(e) => onChange('email', e.target.value)}
          placeholder={t('applicationForm.emailPlaceholder')}
          className={`w-full bg-slate-50 border ${errors.email ? 'border-red-400' : 'border-slate-100'} rounded-xl px-5 py-3.5 text-slate-900 font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all`}
        />
        {errors.email && <p className="text-[11px] font-bold text-red-500 ml-1">{errors.email}</p>}
      </div>

      <div className="space-y-2">
        <label className="text-xs font-bold text-slate-600 uppercase tracking-widest ml-1">{t('applicationForm.phoneLabel')}</label>
        <PhoneInput
          phone={formData.phone}
          countryCode={countryCode}
          onPhoneChange={(phone) => onChange('phone', phone)}
          onCountryCodeChange={onCountryCodeChange}
          hasError={!!errors.phone}
          placeholder={t('applicationForm.phonePlaceholder')}
        />
        {errors.phone && <p className="text-[11px] font-bold text-red-500 ml-1">{errors.phone}</p>}
      </div>
    </div>
  );
});

interface Step2Props {
  file: File | null;
  errors: Record<string, string>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
  onAreaClick: () => void;
  isUploading: boolean;
  uploadError: string | null;
}

const Step2: React.FC<Step2Props> = React.memo(({ file, errors, fileInputRef, onFileChange, onRemoveFile, onAreaClick, isUploading, uploadError }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl sm:text-2xl font-black text-slate-900 mb-2">{t('applicationForm.step2Title')}</h3>
        <p className="text-slate-500">{t('applicationForm.step2Desc')}</p>
      </div>

      <div
        onClick={!isUploading ? onAreaClick : undefined}
        className={`group cursor-pointer border-2 border-dashed ${
          errors.file || uploadError ? 'border-red-300 bg-red-50' : 
          isUploading ? 'border-blue-300 bg-blue-50' :
          'border-slate-200 bg-slate-50 hover:bg-blue-50 hover:border-blue-400'
        } rounded-[32px] p-12 text-center transition-all duration-300 relative`}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={onFileChange}
          accept=".pdf,.doc,.docx"
          disabled={isUploading}
        />

        <div className="max-w-xs mx-auto">
          {isUploading ? (
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 mb-4">
                <svg className="animate-spin w-8 h-8 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <p className="font-bold text-slate-900">{t('applicationForm.uploading', 'Uploading...')}</p>
            </div>
          ) : file ? (
            <div className="flex flex-col items-center">
              <div className={`w-16 h-16 ${uploadError ? 'bg-red-500' : 'bg-blue-600'} rounded-2xl flex items-center justify-center shadow-lg ${uploadError ? 'shadow-red-500/30' : 'shadow-blue-500/30'} mb-4 animate-bounce`}>
                {uploadError ? (
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <p className="font-bold text-slate-900 truncate w-full">{file.name}</p>
              <p className="text-xs text-slate-400 mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              
              {uploadError && <p className="text-xs font-bold text-red-500 mt-2">{uploadError}</p>}
              
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemoveFile(); }}
                className="mt-4 text-xs font-black text-red-500 hover:text-red-600 uppercase tracking-widest"
              >
                {t('applicationForm.removeFile')}
              </button>
            </div>
          ) : (
            <>
              <div className="w-16 h-16 bg-slate-200 group-hover:bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-6 transition-all">
                <svg className="w-8 h-8 text-slate-400 group-hover:text-white transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <h4 className="text-base font-bold text-slate-900 mb-2">{t('applicationForm.dragDrop')}</h4>
              <p className="text-sm text-slate-500 font-medium leading-relaxed">
                {t('applicationForm.fileTypes')}
              </p>
            </>
          )}
        </div>
      </div>
      {errors.file && !uploadError && <p className="text-[11px] font-bold text-red-500 text-center">{errors.file}</p>}
    </div>
  );
});

const ApplicationForm: React.FC<ApplicationFormProps> = ({ job, onBack, onSubmit }) => {
  const { t, i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState<FormStep>(1);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    name: '',
  });
  const [countryCode, setCountryCode] = useState(() => getDefaultCountryCode(i18n.language));
  const [file, setFile] = useState<File | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResumeResponse | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 埋点：表单开始
  useEffect(() => {
    trackApplicationStarted(job.id, job.title);
  }, [job.id, job.title]);

  // 使用 useCallback 优化输入处理函数
  const handleInputChange = useCallback((field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // 清除该字段的错误
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  const handleCountryCodeChange = useCallback((code: string) => {
    setCountryCode(code);
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowedTypes.includes(selectedFile.type)) {
          setErrors({ ...errors, file: t('applicationForm.errors.fileTypeInvalid') });
          return;
        }
        if (selectedFile.size > 10 * 1024 * 1024) {
          setErrors({ ...errors, file: t('applicationForm.errors.fileTooLarge') });
          return;
        }
        setFile(selectedFile);
        setErrors({ ...errors, file: '' });
        setUploadError(null);
        setUploadResult(undefined);

        // 立即开始上传
        setIsUploading(true);
        try {
          const result = await uploadResume(selectedFile);
          setUploadResult(result);
          trackResumeUploaded(job.id, selectedFile.type, Math.round(selectedFile.size / 1024 / 1024 * 100) / 100);
        } catch (error) {
          setUploadError('Upload failed, please try again.');
          // 保留文件，但允许重试
        } finally {
          setIsUploading(false);
        }
      }
    } catch (err) {
        setUploadError('An unexpected error occurred.');
    }
  }, [errors, t]);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    setUploadResult(undefined);
    setUploadError(null);
    if (fileInputRef.current) {
        fileInputRef.current.value = '';
    }
  }, []);

  const handleAreaClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // 验证当前步骤
  const validateCurrentStep = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (currentStep === 1) {
      if (formData.name.length < 2 || formData.name.length > 50) {
        newErrors.name = t('applicationForm.errors.nameRequired');
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = t('applicationForm.errors.emailInvalid');
      }
      if (!formData.phone || formData.phone.length < 5) {
        newErrors.phone = t('applicationForm.errors.phoneRequired');
      }
      if (!agreedToTerms) {
        newErrors.terms = t('applicationForm.errors.termsRequired');
      }
    }

    if (currentStep === 2) {
      if (!file) {
        newErrors.file = t('applicationForm.errors.fileRequired');
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      trackFormValidationError(job.id, currentStep, Object.keys(newErrors));
    }
    return Object.keys(newErrors).length === 0;
  }, [currentStep, formData, file, agreedToTerms, t, job.id]);

  const handleNextStep = useCallback(() => {
    if (validateCurrentStep()) {
      trackApplicationStepCompleted(job.id, currentStep);
      setCurrentStep((prev) => (prev + 1) as FormStep);
      setErrors({});
    }
  }, [validateCurrentStep, job.id, currentStep]);

  const handlePreviousStep = useCallback(() => {
    setCurrentStep((prev) => (prev - 1) as FormStep);
    setErrors({});
  }, []);

  const handleSubmit = useCallback(async () => {
    if (validateCurrentStep()) {
      // 检查文件上传状态
      if (file && !uploadResult && !uploadError) {
          // 正在上传或尚未完成（理论上 isUploading 应该涵盖）
          if (isUploading) {
              alert(t('applicationForm.waitUpload', 'Please wait for the file upload to complete.'));
              return;
          }
          // 应该不会发生，除非上传被跳过
          setUploadError('File not uploaded properly. Please remove and re-add.');
          return;
      }

      if (uploadError) {
          // 上传失败，阻止提交
          return;
      }

      setIsSubmitting(true);
      try {
        // 获取 file_id（拦截器已解包 data 字段）
        // Response structure after interceptor: { file_id: "...", url: "..." }
        const resumeFileId = uploadResult?.file_id;
        
        if (!resumeFileId) {
            setUploadError('Resume upload incomplete. Please try uploading again.');
            return;
        }

        const submitData = {
          job_id: job.numericId,
          job_key: job.id,
          job_title: job.title,
          candidate_name: formData.name,
          candidate_email: formData.email,
          candidate_mobile: formatPhoneNumber(countryCode, formData.phone),
          resume_file_id: resumeFileId,
          resume_url: uploadResult?.url || undefined,  // 简历完整 URL
        };

        const result = await submitApplication(submitData);
        trackApplicationSubmitted(job.id, job.title);
        identifyUser(formData.email, {
          name: formData.name,
          phone: formatPhoneNumber(countryCode, formData.phone),
        });
        onSubmit(result);
      } catch (error) {
        // Error handled by global interceptor
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [validateCurrentStep, onSubmit, file, uploadResult, uploadError, isUploading, isSubmitting, errors, t, countryCode, formData, job]);

  // 进度条组件
  const ProgressBar = useCallback(() => {
    const { t } = useTranslation();
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{t('applicationForm.progress.step2', { step: currentStep })}</span>
          <span className="text-xs font-bold text-blue-600">{t(`applicationForm.progress.estimatedTime${currentStep}`)}</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${(currentStep / 2) * 100}%` }}
          />
        </div>
      </div>
    );
  }, [currentStep]);

  return (
    <div className="pt-24 pb-24 bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="mb-10">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-slate-900 transition-colors mb-6 cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            {t('jobDetail.backToJobs')}
          </button>
          <div className="flex items-center gap-4 mb-4">
             <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white text-xl font-bold">
               u
             </div>
             <div>
               <h1 className="text-3xl font-black text-slate-900 tracking-tight">{t('jobDetail.applyNow')}</h1>
               <p className="text-slate-500 font-medium">{t('jobDetail.department')}: <span className="text-blue-600 font-bold">{job.title}</span></p>
             </div>
          </div>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-xl shadow-2xl shadow-slate-200/50 p-6 md:p-12 border border-slate-100">
          <ProgressBar />

          <div className="min-h-[300px]">
            {currentStep === 1 && <Step1 formData={formData} countryCode={countryCode} errors={errors} onChange={handleInputChange} onCountryCodeChange={handleCountryCodeChange} />}
            {currentStep === 2 && <Step2 file={file} errors={errors} fileInputRef={fileInputRef} onFileChange={handleFileChange} onRemoveFile={handleRemoveFile} onAreaClick={handleAreaClick} isUploading={isUploading} uploadError={uploadError} />}
          </div>

          {/* Terms Checkbox - 只在第1步显示 */}
          {currentStep === 1 && (
            <div className="mt-6">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className="relative flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => {
                      setAgreedToTerms(e.target.checked);
                      if (errors.terms) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.terms;
                          return newErrors;
                        });
                      }
                    }}
                    className="sr-only peer"
                  />
                  <div className={`w-5 h-5 border-2 rounded ${errors.terms ? 'border-red-400' : 'border-slate-300'} peer-checked:border-blue-600 peer-checked:bg-blue-600 transition-all flex items-center justify-center`}>
                    {agreedToTerms && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-xs text-slate-500 group-hover:text-slate-700 transition-colors">
                  <Trans
                    i18nKey="applicationForm.termsCheckbox"
                    components={{
                      termsLink: (
                        <a
                          href={i18n.language === 'zh' ? '/terms-of-use-zh.html' : '/terms-of-use.html'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        />
                      ),
                      privacyLink: (
                        <a
                          href={i18n.language === 'zh' ? '/privacy-policy-zh.html' : '/privacy-policy.html'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        />
                      )
                    }}
                  />
                </span>
              </label>
              {errors.terms && <p className="text-[11px] font-bold text-red-500 mt-2 ml-8">{errors.terms}</p>}
            </div>
          )}

          {/* Actions */}
          <div className="pt-6 flex gap-3">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handlePreviousStep}
                className="flex-1 py-4 rounded-xl font-bold text-base bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all cursor-pointer"
              >
                ← {t('applicationForm.backBtn')}
              </button>
            )}

            {currentStep < 2 ? (
              <button
                type="button"
                onClick={handleNextStep}
                className="flex-1 py-4 rounded-xl font-black text-base bg-slate-900 text-white hover:bg-blue-600 active:scale-95 transition-all shadow-xl cursor-pointer"
              >
                {t('applicationForm.nextBtn')} →
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`flex-1 py-4 rounded-xl font-black text-base transition-all shadow-xl flex items-center justify-center gap-2 cursor-pointer ${isSubmitting ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-slate-900 text-white hover:bg-blue-600 active:scale-95'}`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('applicationForm.submitting')}
                  </>
                ) : (
                  <>
                    {t('applicationForm.submitBtn')}
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ApplicationForm);
